/**
 * Pure token and title helpers shared across the automation surface.
 *
 * A single home for parsing `{{key}}` placeholder tokens into renderable
 * segments and for turning a block into a short plain-text title. Kept
 * dependency-light (no DOM, no React) so both the chip inputs and the compact
 * table cells can reuse it without pulling one another in.
 *
 * @module components/admin/automation/tokens
 */
import { AUTOMATION_PLACEHOLDERS } from '../../../lib/api-contracts/automation/placeholders';
import type { AutomationBlock, AutomationWaitUnit } from '../../../lib/api-contracts/automation-blocks';

/** The set of token keys the UI recognizes as chips. */
const KNOWN_KEYS = new Set(AUTOMATION_PLACEHOLDERS.map((p) => p.key));

/** Token key to its short human label, for chip text and plain titles. */
const LABEL_BY_KEY: Record<string, string> = Object.fromEntries(
  AUTOMATION_PLACEHOLDERS.map((p) => [p.key, p.label]),
);

/** One slice of a template: either literal text or a known token. */
export type TemplateSegment =
  | { kind: 'text'; text: string }
  | { kind: 'token'; key: string; fallback?: string };

/** Matches `{{key}}` and `{{key|default}}` with optional inner whitespace. */
const TOKEN_RE = /\{\{\s*([a-z0-9_]+)(?:\|([^{}|]*))?\s*\}\}/gi;

/**
 * Split a template into text and known-token segments. Unknown tokens are
 * folded back into surrounding text verbatim so a typo never disappears and a
 * future token never renders as a stray chip.
 *
 * @param template The copy that may contain `{{key}}` tokens.
 * @returns Ordered segments that reconstruct the template exactly.
 */
export function parseTemplateSegments(template: string): TemplateSegment[] {
  const segments: TemplateSegment[] = [];
  let lastIndex = 0;
  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(template)) !== null) {
    const key = match[1].toLowerCase();
    // Leave an unknown token embedded in text: do not advance lastIndex past it.
    if (!KNOWN_KEYS.has(key)) continue;
    if (match.index > lastIndex) {
      segments.push({ kind: 'text', text: template.slice(lastIndex, match.index) });
    }
    segments.push({
      kind: 'token',
      key,
      ...(match[2] === undefined ? {} : { fallback: match[2].trim() }),
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < template.length) {
    segments.push({ kind: 'text', text: template.slice(lastIndex) });
  }
  return segments;
}

/**
 * The short label for a token key, or the key itself when unknown.
 *
 * @param key The token key (without braces).
 */
export function placeholderLabel(key: string): string {
  return LABEL_BY_KEY[key] ?? key;
}

/** Singular unit words for a wait block's amount. */
const WAIT_UNIT_SINGULAR: Record<AutomationWaitUnit, string> = {
  hours: 'hour',
  days: 'day',
  business_days: 'business day',
};

/**
 * A human wait title like "Wait 3 days" or "Wait 1 business day".
 *
 * @param amount Positive integer count.
 * @param unit The unit the wait counts in.
 */
export function formatWaitTitle(amount: number, unit: AutomationWaitUnit): string {
  const singular = WAIT_UNIT_SINGULAR[unit];
  const word = amount === 1 ? singular : `${singular}s`;
  return `Wait ${amount} ${word}`;
}

/**
 * A short single-line plain-text title for a block, used in compact cells
 * (the participant table's current-block column). Email tokens collapse to
 * their labels so the title reads as prose.
 *
 * @param block The block to title.
 */
export function blockShortTitle(block: AutomationBlock): string {
  if (block.type === 'wait') return formatWaitTitle(block.amount, block.unit);
  if (block.type === 'email') {
    const plain = parseTemplateSegments(block.subject)
      .map((seg) => (seg.kind === 'text' ? seg.text : placeholderLabel(seg.key)))
      .join('')
      .trim();
    return plain || 'Email';
  }
  return block.type;
}
